/**
 * Fetch the chaostree -> Interspacial handoff token.
 *
 * Same-origin in production (the game is a page on chaostree.xyz), so the
 * session cookie rides along automatically — `credentials: 'include'` is what
 * makes that work through the dev proxy too.
 */
/**
 * No `/v1` in the path, despite the router being called `apiV1Router`.
 *
 * ChaosTree mounts it with `app.use('/api', apiV1Router)` — the name is
 * aspirational and the URL is not versioned. This was written from the variable
 * name and 404d on every request, which reads as "not signed in" from the
 * outside and is a miserable thing to chase.
 */
const TOKEN_ENDPOINT = '/api/games/interspacial/token';

export class NotSignedInError extends Error {
  constructor() {
    super('Not signed in to ChaosTree');
    this.name = 'NotSignedInError';
  }
}

/**
 * How long a handoff token lasts, as chaostree issues it.
 *
 * Two minutes. Not a guess -- `GAME_TOKEN_TIMER` in the chaostree backend --
 * and short enough that "fetch it once at startup" is wrong within the first
 * minute of play. It was fetched once at startup.
 */
export const GAME_TOKEN_LIFETIME_MS = 120_000;

/**
 * Renew this far before expiry, in milliseconds.
 *
 * A request issued at the last instant still has to reach the server, so a
 * token that is technically alive when sent can be dead when read. Half the
 * lifetime is generous and costs one request a minute.
 */
export const RENEW_BEFORE_MS = 60_000;

/**
 * When a token expires, in epoch milliseconds, or null if it does not say.
 *
 * Reads `exp` without verifying anything -- the signature is the server's
 * business, and this only decides when to ask for a new one.
 */
export function expiryOf(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const claims = JSON.parse(atob(padded)) as { exp?: number };

    return typeof claims.exp === 'number' ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Whether a token is close enough to expiry to be worth replacing. */
export function needsRenewal(token: string, now = Date.now()): boolean {
  const expires = expiryOf(token);
  // A token that will not say when it dies is treated as dying now: better one
  // needless request a minute than a session that silently stops saving.
  if (expires === null) return true;
  return expires - now <= RENEW_BEFORE_MS;
}

export async function fetchGameToken(): Promise<string> {
  const response = await fetch(TOKEN_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
  });

  if (response.status === 401) throw new NotSignedInError();
  if (!response.ok) throw new Error(`Token request failed (${response.status})`);

  const body = (await response.json()) as { success?: boolean; token?: string };
  if (!body.token) throw new Error('Token request returned no token');

  return body.token;
}
